import React from "react";

import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from "@chakra-ui/react";

import { FiSearch } from "react-icons/fi";
import { IoCopySharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

import { ConfirmModal } from "@/components/common/controls/ConfirmModal";
import Barrier from "@/components/common/permission/Barrier";
import { Paginate } from "@/components/common/table/Paginate";
import { EmailForwardingSenders } from "@/components/features/client/EmailForwardingSenders";
import { AddDepartment } from "@/components/features/settings/AddDepartment";
import { EmptyState } from "@/components/features/settings/EmptyState";
import { MemberActionsMenu } from "@/components/features/settings/MemberActionsMenu";
import { MemberDetails } from "@/components/features/settings/MemberDetails";
import { useClientTeam } from "@/hooks/features/useClientTeam";
import { useCompany } from "@/hooks/util/useCompany";

export interface ClientPractiseTeamProps {}

export const ClientPractiseTeam: React.FC<ClientPractiseTeamProps> = () => {
  const navigate = useNavigate();
  const { activeClientId, company } = useCompany();
  const data = useClientTeam({ clientId: activeClientId });
  const {
    teamMembers,
    page,
    searchQuery,
    selectedMember,
    isTeamMembersLoading,
    isRemovingMember,
    availablePlatforms,
    viewMemberDisclosure,
    deleteMemberDisclosure,
    addDepartmentDisclosure,
    debouncedOnChange,
    handlePageClick,
    triggerAddMember,
    openMemberDetails,
    handleDeleteMember,
    triggerDeleteWarning,
    handleCopy,
    onMemberMenuClick,
    handleInviteResend
  } = data;

  return (
    <>
      <Box mt={8} rounded={16} px={10} pt={7} pb={16} bg="white">
        <HStack justifyContent="space-between">
          <Box>
            <Heading fontSize={16} mb={1} fontWeight={600}>
              Manage {company?.name} Team
            </Heading>
            <Text color="secondary.700" fontSize={13}>
              Manage your team and their permissions
            </Text>
          </Box>

          <HStack spacing={4}>
            <Barrier show when="company.departments.create">
              <Button
                onClick={addDepartmentDisclosure.onOpen}
                variant="outline"
                h={9}
                size="sm"
              >
                Create Department
              </Button>
            </Barrier>

            <Barrier show when="company.members.create">
              <Button onClick={triggerAddMember} h={9} size="sm">
                Invite people
              </Button>
            </Barrier>
          </HStack>
        </HStack>

        {!!company?.forwarding_email && (
          <HStack
            p={4}
            mt={10}
            borderWidth="1px"
            borderRadius="md"
            bg="#F7F5F9"
            justify="space-between"
          >
            <Text fontSize={14} fontWeight={500}>
              This is your unique forwarding email address. Send documents
              directly to Jack to work on.
            </Text>

            <InputGroup maxW={410}>
              <Input
                value={company?.forwarding_email}
                isReadOnly
                borderRadius="md"
                bg="white"
                minWidth="100px"
                fontWeight={600}
                fontSize={14}
              />
              <InputRightElement
                display="flex"
                alignItems="center"
                justifyContent="center"
                h="full"
              >
                <IconButton
                  aria-label="Copy"
                  variant="default"
                  icon={<IoCopySharp />}
                  bg="inherit"
                  color="black"
                  borderRadius="md"
                  _hover={{ bg: "blackAlpha.50" }}
                  boxSize="2rem"
                  onClick={handleCopy}
                />
              </InputRightElement>
            </InputGroup>
          </HStack>
        )}

        <HStack mt={30} justify="space-between">
          <HStack w="full">
            <InputGroup w="full" maxW={401} borderColor="blackAlpha.300" h={9}>
              <InputLeftElement top={-0.5} pointerEvents="none">
                <FiSearch fontSize={18} color="gray.500" />
              </InputLeftElement>
              <Input
                onChange={(event) => {
                  debouncedOnChange(event.target.value);
                }}
                variant="filled"
                fontSize={14}
                h={9}
                type="search"
                placeholder="Search by name or email"
              />
            </InputGroup>

            <Select w="fit-content" variant="filled" size="sm" h={9}>
              <option value="all">All</option>
            </Select>
          </HStack>
        </HStack>

        <Flex
          display={["flex", "flex", "flex", "flex", "block"]}
          direction="row"
          w="full"
          overflow={["auto"]}
        >
          <TableContainer flexShrink={0}>
            <Table mt={10} size="sm">
              <Thead>
                <Tr>
                  <Th
                    pl={0}
                    color="black"
                    textTransform="capitalize"
                    fontWeight={600}
                  >
                    First Name
                  </Th>
                  <Th color="black" textTransform="capitalize" fontWeight={600}>
                    Last Name
                  </Th>
                  <Th color="black" textTransform="capitalize" fontWeight={600}>
                    Email
                  </Th>
                  <Th color="black" textTransform="capitalize" fontWeight={600}>
                    Connections
                  </Th>
                  <Th color="black" textTransform="capitalize" fontWeight={600}>
                    Position
                  </Th>
                  <Th color="black" textTransform="capitalize" fontWeight={600}>
                    Role
                  </Th>
                  <Th color="black" textTransform="capitalize" fontWeight={600}>
                    Invitation Status
                  </Th>
                  <Th color="black" textTransform="capitalize" fontWeight={600}>
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {teamMembers?.items?.map((member) => (
                  <Tr key={member.id} _hover={{ bg: "blackAlpha.50" }}>
                    <Td pl={0} fontWeight={500} py={4}>
                      {member.user.first_name}
                    </Td>
                    <Td fontWeight={500} py={4}>
                      {member.user.last_name}
                    </Td>
                    <Td fontWeight={500} py={4}>
                      {member.user.email}
                    </Td>
                    <Td fontWeight={500} py={4}>
                      <HStack cursor="pointer" spacing={4}>
                        {availablePlatforms.map(({ category, name }) => {
                          const connection = member.connections.find(
                            (c) => c.platform === name
                          );
                          const isConnected =
                            name === "whatsapp"
                              ? member.whatsapp_connection?.status ===
                                "connected"
                              : connection?.status === "connected";
                          return (
                            <Image
                              onClick={() =>
                                navigate(
                                  `/client/${activeClientId}/settings/connections/${category}/${name}`
                                )
                              }
                              key={name}
                              alt={name}
                              style={{
                                filter: isConnected ? "none" : "grayscale(1)"
                              }}
                              src={`/images/${name}.svg`}
                              width={18}
                              height={18}
                            />
                          );
                        })}
                      </HStack>
                    </Td>
                    <Td fontWeight={500} py={4}>
                      {member.position}
                    </Td>
                    <Td fontWeight={500} py={4}>
                      <Flex
                        w="fit-content"
                        rounded="full"
                        fontWeight={600}
                        bg="rgba(223, 55, 99, 0.26);"
                        px={2.5}
                        py={0.5}
                      >
                        {member.role?.replace("_", " ")}
                      </Flex>
                    </Td>
                    <Td fontWeight={500} py={4}>
                      {member.user.is_email_confirmed ? "Accepted" : "Sent"}
                    </Td>
                    <Td fontWeight={500} py={4}>
                      <MemberActionsMenu
                        member={member}
                        onMemberMenuClick={onMemberMenuClick}
                        openMemberDetails={openMemberDetails}
                        handleInviteResend={handleInviteResend}
                        triggerDeleteWarning={triggerDeleteWarning}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            {!teamMembers.items.length &&
              !searchQuery &&
              !isTeamMembersLoading && (
                <EmptyState
                  title="Invite a team member"
                  ctaText="Invite people"
                  ctaAction={triggerAddMember}
                  description="When you invite someone, they will appear here"
                  mt={16}
                />
              )}

            <Box mt={6}>
              <Paginate
                page={page}
                data={teamMembers}
                handlePageClick={handlePageClick}
              />
            </Box>
          </TableContainer>
        </Flex>

        {addDepartmentDisclosure.isOpen && <AddDepartment {...data} />}

        {viewMemberDisclosure.isOpen && <MemberDetails {...data} />}

        <ConfirmModal
          {...deleteMemberDisclosure}
          loadingText="Deleting..."
          isLoading={isRemovingMember}
          confirmText="Delete"
          title={`Are you sure want to permanently delete ${selectedMember?.user.first_name} ${selectedMember?.user.last_name}?`}
          content={` `}
          confirmAction={handleDeleteMember}
        />
      </Box>
      <Box mt={8} rounded={16} px={0} pt={0} pb={16} bg="white">
        <EmailForwardingSenders />
      </Box>
    </>
  );
};
